import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase/firebase';
import { collection, query, where, getDocs, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import WIcon from '../../components/shared/WIcon';
import Modal from '../../components/shared/Modal';

const TIER_LABELS = ['', 'All Staff', 'Manager', 'Executive'];
const TIER_COLORS = ['', '#10b981', '#00bf63', '#f59e0b'];

const initialsFor = (name, surname) => {
  const parts = [name, surname].filter(Boolean);
  if (parts.length === 0) return '—';
  return parts.slice(0, 2).map(p => p[0]).join('').toUpperCase();
};

const CompanyEmployees = () => {
  const navigate = useNavigate();
  const [companyData, setCompanyData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const loadEmployees = useCallback(async (companyId) => {
    try {
      const snap = await getDocs(query(collection(db, 'users'), where('companyId', '==', companyId)));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const da = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
        const dbb = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
        return dbb - da;
      });
      setEmployees(list);
    } catch (err) {
      console.error('Error loading employees:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem('companyData');
    if (!stored) {
      navigate('/company/login');
      return;
    }
    const company = JSON.parse(stored);
    setCompanyData(company);
    loadEmployees(company.id);
  }, [navigate, loadEmployees]);

  const filtered = useMemo(() => {
    let list = employees;
    if (tierFilter !== 'all') {
      list = list.filter((e) => String(e.tier || 1) === tierFilter);
    }
    if (search) {
      const s = search.toLowerCase();
      list = list.filter((e) =>
        (e.name || '').toLowerCase().includes(s) ||
        (e.surname || '').toLowerCase().includes(s) ||
        (e.phone || '').toLowerCase().includes(s) ||
        (e.email || '').toLowerCase().includes(s),
      );
    }
    return list;
  }, [employees, tierFilter, search]);

  const updateTier = async (employeeId, newTier) => {
    try {
      await updateDoc(doc(db, 'users', employeeId), { tier: parseInt(newTier, 10) });
      setEmployees((prev) => prev.map(e => e.id === employeeId ? { ...e, tier: parseInt(newTier, 10) } : e));
    } catch (err) {
      console.error('Error updating tier:', err);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteDoc(doc(db, 'users', confirmDelete.id));
      setEmployees((prev) => prev.filter(e => e.id !== confirmDelete.id));
      setConfirmDelete(null);
    } catch (err) {
      console.error('Error deleting employee:', err);
    }
  };

  const formatJoined = (timestamp) => {
    if (!timestamp) return '—';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const exportCsv = () => {
    const rows = [
      ['Name', 'Phone', 'Email', 'Tier', 'Joined'],
      ...filtered.map((e) => [
        [e.name, e.surname].filter(Boolean).join(' '),
        e.phone || '',
        e.email || '',
        e.tier || 1,
        formatJoined(e.createdAt),
      ]),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${companyData?.name || 'company'}_employees.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const tierFilters = [
    { id: 'all', label: 'All' },
    { id: '1', label: 'Tier 1' },
    { id: '2', label: 'Tier 2' },
    { id: '3', label: 'Tier 3' },
  ];

  if (loading) {
    return <div style={{ padding: 40, color: '#64748b', fontSize: 14 }}>Loading employees…</div>;
  }

  return (
    <>
      {confirmDelete && (
        <Modal title="Delete employee" onClose={() => setConfirmDelete(null)} width={420}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.6, margin: 0 }}>
              Remove <strong>{[confirmDelete.name, confirmDelete.surname].filter(Boolean).join(' ') || 'this employee'}</strong> from your company directory? This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="cancel-btn" style={{ flex: 1 }} onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button type="button" className="btn btn-danger" style={{ flex: 1, justifyContent: 'center' }} onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </Modal>
      )}

      <div className="loysta-card">
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 7, padding: '7px 12px' }}>
            <WIcon name="search" size={15} color="#94a3b8" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search employees…"
              style={{ border: 'none', background: 'none', fontSize: 13, fontFamily: 'inherit', color: '#0f172a', outline: 'none', width: '100%' }}
            />
          </div>
          {tierFilters.map((t) => {
            const active = tierFilter === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTierFilter(t.id)}
                style={{
                  padding: '7px 14px',
                  borderRadius: 7,
                  border: `1.5px solid ${active ? '#00bf63' : '#e2e8f0'}`,
                  background: active ? '#00bf63' : 'white',
                  color: active ? 'white' : '#334155',
                  fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >{t.label}</button>
            );
          })}
          <button
            type="button"
            onClick={exportCsv}
            disabled={filtered.length === 0}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '7px 12px', border: '1.5px solid #e2e8f0', borderRadius: 7,
              background: 'white', color: '#334155',
              fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
              cursor: filtered.length === 0 ? 'not-allowed' : 'pointer',
              opacity: filtered.length === 0 ? 0.5 : 1,
              whiteSpace: 'nowrap',
            }}
          >
            <WIcon name="download" size={14} color="#64748b" />
            Export
          </button>
          <button
            type="button"
            onClick={() => navigate('/company/add-employee')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', background: '#00bf63', color: 'white',
              border: 'none', borderRadius: 8,
              fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#009950'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#00bf63'; }}
          >
            <WIcon name="plus" size={14} color="white" />
            Add Employee
          </button>
        </div>

        <table className="loysta-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Tier</th>
              <th>Joined</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="6" style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
                {employees.length === 0
                  ? 'No employees yet. Click "Add Employee" to onboard your first one.'
                  : 'No employees match your filters.'}
              </td></tr>
            ) : filtered.map((e) => {
              const t = Number(e.tier) || 1;
              return (
                <tr key={e.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%',
                        background: `linear-gradient(135deg, ${TIER_COLORS[t]}, ${t === 3 ? '#fbbf24' : t === 2 ? '#c1ff72' : '#6ee7b7'})`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700, color: '#0f172a', flexShrink: 0,
                      }}>{initialsFor(e.name, e.surname)}</div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                          {[e.name, e.surname].filter(Boolean).join(' ') || 'Unnamed'}
                        </div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>ID: {e.id.slice(-8)}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ color: '#334155' }}>{e.phone || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                  <td style={{ color: '#64748b' }}>{e.email || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                  <td>
                    <select
                      value={t}
                      onChange={(ev) => updateTier(e.id, ev.target.value)}
                      style={{
                        background: 'white', border: '1.5px solid #e2e8f0', borderRadius: 7,
                        padding: '5px 10px', fontSize: 12, fontWeight: 600,
                        color: TIER_COLORS[t], fontFamily: 'inherit', cursor: 'pointer', outline: 'none',
                      }}
                    >
                      <option value={1}>Tier 1 — All Staff</option>
                      <option value={2}>Tier 2 — Manager</option>
                      <option value={3}>Tier 3 — Executive</option>
                    </select>
                  </td>
                  <td style={{ color: '#94a3b8', fontSize: 12 }}>{formatJoined(e.createdAt)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 2 }}>
                      <button className="loysta-icon-btn danger" onClick={() => setConfirmDelete(e)} title="Delete">
                        <WIcon name="trash" size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div style={{ padding: '12px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: '#94a3b8' }}>
          <span>Showing {filtered.length} of {employees.length} {employees.length === 1 ? 'employee' : 'employees'}</span>
        </div>
      </div>
    </>
  );
};

export default CompanyEmployees;
